import { DatePipe } from '@angular/common';
import { Component, computed, inject, linkedSignal, OnDestroy, output, signal } from '@angular/core';
import { AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Subject, takeUntil } from 'rxjs';
import {
  compareCropFieldByAdvice,
  compareCropFieldByLastSprayDate,
  compareCropFieldByName,
  CropFieldComparator,
  SortMethodType,
} from 'src/app/shared/components/disease-overview-table.component/helpers/sort.helpers';
import {
  AdviceMouseClickEvent,
  CropFieldAdvice,
} from 'src/app/shared/components/disease-overview-table.component/models/models';
import { VirusMonitorSignalStore } from 'src/app/virus-monitor/signal-store/virus-monitor-signal-store';
import { createVirusMonitorTelemetryProperties, VirusMonitorAIIdentifiers } from '../../../../shared/constants';
import { DiseaseOverviewTableComponent } from '../../../../../shared/components/disease-overview-table.component/table.component';
import { RecalculateButtonComponent } from '../../../../../shared/components/recalculate-button/recalculate-button.component';
import { SprayLegendComponent } from 'src/app/shared/components/spray-legend/spray-legend.component';
import { VirusMonitorActionMenuComponent } from '../../../../shared/action-menu.component';
import { DateService } from '../../../../../shared/services/date.service';
import { CropYearService } from '../../../../../shared/services/crop-year.service';

@Component({
  selector: 'app-virus-monitor-advice-overview',
  templateUrl: './overview.component.html',
  styleUrl: './overview.component.scss',
  imports: [
    RecalculateButtonComponent,
    VirusMonitorActionMenuComponent,
    DiseaseOverviewTableComponent,
    DatePipe,
    TranslatePipe,
    SprayLegendComponent,
  ],
})
export class OverviewComponent implements OnDestroy {
  public readonly adviceClick = output<AdviceMouseClickEvent>();
  protected readonly SortMethodType = SortMethodType;
  private readonly cropYearService = inject(CropYearService);
  protected readonly isCropYearReadOnly = this.cropYearService.isReadOnly;
  protected readonly store = inject(VirusMonitorSignalStore);
  protected readonly lastCalculatedOn = this.store.virusMonitorCalculationDate;
  protected readonly advices = computed((): CropFieldAdvice[] => this.store.virusMonitorAdvices());
  private readonly dateService = inject(DateService);
  protected readonly selectedDates = computed(() => {
    const fromDate = this.store.fromDate();
    const toDate = this.store.toDate();
    return fromDate && toDate ? this.dateService.generateDaysBetweenDates(fromDate, toDate) : [];
  });

  private readonly visibilityOverrides = signal<Map<number, boolean>>(new Map());

  protected readonly diseaseCropFields = linkedSignal<
    { id: number; name: string; areaInHectares: number; isVisible: boolean }[] | undefined,
    { id: number; name: string; areaInHectares: number; isVisible: boolean }[]
  >({
    source: () => {
      if (this.store.virusMonitorRegistrationsIsLoading()) return undefined;
      const overrides = this.visibilityOverrides();
      return this.store.virusMonitorAdvices().map((advice) => ({
        id: advice.cropFieldId,
        name: advice.cropFieldName,
        areaInHectares: advice.amountOfHectares,
        isVisible: overrides.has(advice.cropFieldId)
          ? (overrides.get(advice.cropFieldId) ?? advice.isCropFieldVisible)
          : advice.isCropFieldVisible,
      }));
    },
    computation: (source, previous) => source ?? previous?.value ?? [],
  });
  protected readonly isRecalculationDisabled = linkedSignal<boolean | undefined, boolean>({
    source: () => {
      if (this.store.virusMonitorRegistrationsIsLoading()) return undefined;
      return (
        this.store.isVirusMonitorCropFieldsSyncing() ||
        this.advices().length <= 0 ||
        this.isCropYearReadOnly() ||
        !this.store.hasEligibleVirusMonitorCropFields()
      );
    },
    computation: (source, previous) => source ?? previous?.value ?? true,
  });
  private readonly localeService = inject(LocaleService);
  protected locale: string = this.localeService.getLocale();
  private readonly ai = inject(AppInsightsServiceInjector);
  private readonly destroy$ = new Subject<void>();
  protected readonly selectedAdvices = signal<CropFieldAdvice[]>([]);
  private previousSelectedDates: Date[] = [];

  constructor() {
    this.localeService.localeDataChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.locale = this.localeService.getLocale();
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected onCropFieldClick(event: AdviceMouseClickEvent): void {
    this.ai.trackEvent(
      VirusMonitorAIIdentifiers.overviewDetailOpened,
      createVirusMonitorTelemetryProperties('overviewDetailOpened', { cropFieldId: event.cropField.cropFieldId }),
    );
    this.adviceClick.emit(event);
    this.store.selectCropField(event.cropField.cropFieldId);
  }

  protected onSelectedDatesChange(dates: Date[]): void {
    this.ai.trackEvent(
      VirusMonitorAIIdentifiers.overviewDateNavigationClicked,
      createVirusMonitorTelemetryProperties('overviewDateNavigationClicked', {
        direction: this.resolveDateNavigationDirection(dates),
      }),
    );

    this.previousSelectedDates = dates;
    this.store.patchSelectedDates(dates);
  }

  protected onSelectedCropFieldsChange(selectedCropFields: CropFieldAdvice[]): void {
    this.ai.trackEvent(
      VirusMonitorAIIdentifiers.overviewCropFieldSelectionChanged,
      createVirusMonitorTelemetryProperties('overviewCropFieldSelectionChanged', {
        cropFieldCount: selectedCropFields.length,
      }),
    );
    this.selectedAdvices.set(selectedCropFields);
  }

  protected onCropFieldVisibilityChanged(event: { cropFieldId: number; isVisible: boolean }): void {
    this.ai.trackEvent(
      VirusMonitorAIIdentifiers.overviewCropFieldSelectionChanged,
      createVirusMonitorTelemetryProperties('overviewCropFieldSelectionChanged', {
        cropFieldId: event.cropFieldId,
        isVisible: event.isVisible,
      }),
    );

    const overrides = new Map(this.visibilityOverrides());
    overrides.set(event.cropFieldId, event.isVisible);
    this.visibilityOverrides.set(overrides);
  }

  protected onRecalculate(): void {
    if (this.isCropYearReadOnly()) {
      return;
    }

    this.ai.trackEvent(
      VirusMonitorAIIdentifiers.overviewRecalculateAllClicked,
      createVirusMonitorTelemetryProperties('overviewRecalculateAllClicked'),
    );
    const eligibleCropFieldIds = this.store.eligibleVirusMonitorCropFieldIds();
    this.store.recalculateVirusMonitor(eligibleCropFieldIds);
  }

  protected onSortMethodChanged(sortMethod: CropFieldComparator): void {
    this.ai.trackEvent(
      VirusMonitorAIIdentifiers.overviewSortChanged,
      createVirusMonitorTelemetryProperties('overviewSortChanged', { sortOption: this.resolveSortOption(sortMethod) }),
    );
  }

  protected onActionMenuOpened(): void {
    this.ai.trackEvent(
      VirusMonitorAIIdentifiers.overviewActionMenuOpened,
      createVirusMonitorTelemetryProperties('overviewActionMenuOpened'),
    );
  }

  private resolveSortOption(sortMethod: CropFieldComparator): string {
    if (sortMethod === compareCropFieldByAdvice) {
      return SortMethodType.Advice;
    }

    if (sortMethod === compareCropFieldByLastSprayDate) {
      return SortMethodType.Spray;
    }

    if (sortMethod === compareCropFieldByName) {
      return SortMethodType.Name;
    }

    return 'custom';
  }

  private resolveDateNavigationDirection(dates: Date[]): 'next' | 'previous' | 'unknown' {
    if (dates.length < 2 || this.previousSelectedDates.length < 2) {
      return 'unknown';
    }

    const currentStart = dates[0]?.getTime() ?? 0;
    const previousStart = this.previousSelectedDates[0]?.getTime() ?? 0;

    if (currentStart > previousStart) {
      return 'next';
    }

    if (currentStart < previousStart) {
      return 'previous';
    }

    return 'unknown';
  }
}
